import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaServer, FaShieldAlt, FaHome, FaCogs } from "react-icons/fa";
import ProjectModal from "./ProjectModal";

const projects = [
  {
    name: "PMO Watch Tower",
    description:
      "Designed a system to track Total Project Cost (TPC), contract details (SPK), and categorized cash outflows. Enabled real-time analysis of budget usage to assess and improve allocation efficiency throughout project execution.",
    impact: [
      "Streamlined budget monitoring across multiple projects",
      "Reduced manual reporting effort with automated real-time dashboards",
      "Improved cost allocation visibility for management decision-making",
    ],
    techstack: ["Go", "PostgreSQL", "React.JS", "Tailwind", "JWT"],
    icon: <FaServer />,
    note: "Internal project",
  },
  {
    name: "BRICommando",
    description:
      "Built an internal audit dashboard to monitor initiative performance, compliance metrics and data integrity across departments. Developed backend services with secure JWT-based authentication and dynamic reporting endpoints.",
    impact: [
      "Centralized audit tracking across multiple departments",
      "Enhanced compliance monitoring with automated metric calculations",
      "Enabled dynamic reporting for cross-department data analysis",
    ],
    techstack: ["Go", "PostgreSQL", "JWT"],
    icon: <FaShieldAlt />,
    note: "Internal project",
  },
  {
    name: "BEERP",
    description:
      "Developed and maintained a modular ERP system handling HR, finance, and project management workflows. Implemented RESTful APIs for leave management, requisitions and approval processes with role-based access and real-time FCM notifications.",
    impact: [
      "Modular design supporting HR, finance & project management in one system",
      "Implemented role-based access control for multi-level approval workflows",
      "Integrated Firebase Cloud Messaging for real-time push notifications",
    ],
    techstack: ["PHP", "MySQL", "Firebase", "JWT"],
    icon: <FaCogs />,
    note: "Internal project",
  },
  {
  name: "BEERP 2.0",
  description: "Building and maintaining a multi-tenant modular ERP SaaS platform (HRM, Asset Management, Vendor Management, Sales/CRM) with NestJS and PostgreSQL. Currently leading end-to-end development of a Vendor Management module — a multi-stage approval workflow system covering vendor registration, document verification, tiered review (AVP/EVP/Legal), site survey, and contract lifecycle management.",
  impact: [
    "Designed and shipped a full vendor lifecycle system with multi-stage approval workflow (Procurement verification, AVP, EVP, Legal review, survey, contract approval), including a complete audit trail for compliance",
    "Built a row-level security architecture for an external vendor-facing portal, isolating vendor auth/data access from internal staff systems while sharing the same backend",
    "Engineered a consolidated notification pipeline (in-app, email, and permission-targeted internal alerts) that cut redundant vendor emails by up to 75% per correction request",
    "Implemented granular RBAC combining role-based permissions with per-user/position overrides across HR, Asset Management, and Vendor Management modules",
    "Designed multi-document per-contract approval, restricting sign-off to a specifically assigned approver (not just permission-based) for stronger accountability"
  ],
  techstack: ["NestJS", "TypeScript", "Prisma", "PostgreSQL", "Next.js", "Firebase Cloud Messaging", "JWT"],
  icon: <FaCogs />,
  note: "Internal project"
}
];

const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  return (
    <section className="relative overflow-hidden py-16 sm:py-24">
      <div className="absolute inset-0 bg-dark-950" />
      <div className="absolute inset-0 grid-overlay" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          className="mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="font-mono text-xs text-gray-400 block mb-3">
            {"// "}section.projects
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
            My <span className="text-glow">Projects</span>
          </h2>
          <div className="section-divider mt-4 max-w-xs" />
        </motion.div>

        <div className="grid gap-5 sm:gap-6 grid-cols-1 lg:grid-cols-2">
          {projects.map((project, index) => (
            <motion.div
              key={project.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className="group cursor-pointer"
              onClick={() => setSelectedProject(project)}
            >
              <div className="glass-card p-5 sm:p-6 h-full relative overflow-hidden">
                {/* Corner accent */}
                <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                  <div className="absolute top-0 right-0 w-px h-8 bg-sky-400/20 group-hover:bg-sky-400/50 transition-colors duration-500" />
                  <div className="absolute top-0 right-0 h-px w-8 bg-sky-400/20 group-hover:bg-sky-400/50 transition-colors duration-500" />
                </div>

                {/* Header */}
                <div className="flex items-center gap-3 mb-3 sm:mb-4">
                  <span className="text-sky-400/60 group-hover:text-sky-400 transition-colors duration-300 text-lg">
                    {project.icon}
                  </span>
                  <h3 className="font-mono text-sm sm:text-base font-semibold text-white group-hover:text-sky-300 transition-colors duration-300">
                    {project.name}
                  </h3>
                </div>

                {/* Description */}
                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed mb-4">
                  {project.description}
                </p>

                {/* Impact highlights */}
                <div className="mb-4 space-y-1.5">
                  <span className="font-mono text-[10px] text-sky-400/70 block mb-2">
                    {"// "}key impact
                  </span>
                  {project.impact.map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="font-mono text-sky-400/50 text-[10px] mt-0.5 shrink-0">&#9656;</span>
                      <p className="text-gray-400 text-xs leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>

                {/* Tech stack */}
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
                  {project.techstack.map((tech) => (
                    <span key={tech} className="tech-badge">
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Note */}
                {project.note && (
                  <p className="font-mono text-[10px] text-gray-500 italic">
                    * {project.note} — demo available upon request
                  </p>
                )}

                {/* Bottom line */}
                <div className="absolute bottom-0 left-0 w-0 h-px bg-sky-400/50 group-hover:w-full transition-all duration-700" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <ProjectModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
      />
    </section>
  );
};

export default Portfolio;
